import { isAfter, parseISO } from 'date-fns';

import type {
  FilterOperator,
  SelectionListSelectedItem,
} from '../../data-builder.store';
import type { CustomFilterDefinition } from '../../datasets/deliverable-column-config';
import {
  isInBetweenOperator,
  isNullaryOperator,
} from './string-filter-operators';

type ConditionDraftForValidation = {
  filterDefId: string | null;
  field: string | null;
  operator: FilterOperator | null;
  value: string[];
  modifier: string | null;
  selectionListItems?: SelectionListSelectedItem[];
};

type ConditionDraftForSerialization = {
  type: string | null;
  filterDefId: string | null;
  field: string | null;
  groupKey: string | null;
  operator: FilterOperator | null;
  value: string[];
  modifier: string | null;
  selectionListItems?: SelectionListSelectedItem[];
};

export function isConditionValid(
  condition: ConditionDraftForValidation,
  getFilterDef: (defId: string | null) => CustomFilterDefinition | undefined
): boolean {
  if (condition.field === null || condition.operator === null) {
    return false;
  }

  if (isNullaryOperator(condition.operator)) {
    return true;
  }

  const filterDef = getFilterDef(condition.filterDefId);

  if (filterDef?.modifiers.length && condition.modifier === null) {
    return false;
  }

  if (filterDef?.type === 'selection_list') {
    return (
      condition.selectionListItems !== undefined &&
      condition.selectionListItems.length > 0
    );
  }

  if (filterDef?.type === 'numeric') {
    if (isInBetweenOperator(condition.operator)) {
      return (
        condition.value.length >= 2 &&
        condition.value[0] !== '' &&
        condition.value[1] !== ''
      );
    }

    return condition.value.length > 0 && condition.value[0] !== '';
  }

  if (filterDef?.type === 'date') {
    if (isInBetweenOperator(condition.operator)) {
      const startValue = condition.value[0];
      const endValue = condition.value[1];

      if (
        condition.value.length < 2 ||
        startValue === undefined ||
        endValue === undefined ||
        startValue === '' ||
        endValue === ''
      ) {
        return false;
      }

      const startDate = parseISO(startValue);
      const endDate = parseISO(endValue);
      return !isAfter(startDate, endDate);
    }

    if (
      !filterDef.operators.includes(
        condition.operator as (typeof filterDef.operators)[number]
      )
    ) {
      return false;
    }

    return condition.value.length > 0 && condition.value[0] !== '';
  }

  if (filterDef?.type === 'boolean') {
    return true;
  }

  return condition.value.length > 0;
}

export function serializeConditionDrafts(
  conditions: ConditionDraftForSerialization[]
) {
  return conditions.map((condition) => ({
    type: condition.type,
    filterDefId: condition.filterDefId,
    field: condition.field,
    groupKey: condition.groupKey,
    operator: condition.operator,
    value: condition.value,
    modifier: condition.modifier,
    selectionListItems:
      condition.selectionListItems?.map((item) => ({
        id: item.id,
        label: item.label,
        selectionListId: item.selectionListId,
      })) ?? null,
  }));
}
